import { useEffect, useState } from "react";

// MUI
import { FormControlLabel, Radio, RadioGroup, Stack, Typography } from "@mui/material";

// Redux
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store.ts";

// Third Party
import clsx from "clsx";
import { useMutation, useQuery } from "@tanstack/react-query";

// Queries
import { getDataSourceList } from "@Settings/api/AdministrationTools/queries.ts";

// Mutations
import { updateAdministrationTools } from "@Settings/api/AdministrationTools";

// Types
import { IAdministrationTools, IIntegration } from "@Settings/types/administrationTools/model.ts";

interface IDealerManagementDataSourceProps {
	integrations: IIntegration;
}

const DealerManagementDataSource = (props: IDealerManagementDataSourceProps) => {
	const { integrations } = props;
	const [dataSource, setDataSource] = useState<string | null>(null);

	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId;

	/******************************************************* QUERIES ********************************************************/
	const { data: dataSourceList } = useQuery({
		queryKey: ["dmsDataSourceList", locationId, integrations?.dmsProvider?.providerName],
		queryFn: async () => getDataSourceList(locationId as string, "DMS", integrations?.dmsProvider?.providerName),
		enabled: !!locationId && !!integrations?.dmsProvider?.providerName,
		staleTime: Infinity,
	});

	/******************************************************* MUTATIONS ******************************************************/
	const { mutate: mutateDMS, isPending } = useMutation({
		mutationKey: ["administrationToolsDataSource", locationId],
		mutationFn: (input: Partial<IAdministrationTools>) => updateAdministrationTools(locationId as string, input),
		onMutate: (input: Partial<IAdministrationTools>) => {
			const previousValue = integrations?.dmsProvider?.dataSource;
			const newValue = input.integrations?.dmsProvider?.dataSource;
			setDataSource(newValue as string);
			return { previousValue, newValue };
		},
		onError: (_err, _newValue, context) => {
			setDataSource(context?.previousValue as string);
		},
	});

	/******************************************************* HANDLERS *******************************************************/
	const handlerDataSourceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		const input = {
			integrations: {
				...integrations,
				dmsProvider: {
					...integrations?.dmsProvider,
					dataSource: value,
				},
			},
		};
		mutateDMS(input);
	};

	useEffect(() => {
		setDataSource(integrations?.dmsProvider?.dataSource);
	}, [integrations?.dmsProvider?.dataSource]);

	if (!dataSourceList?.length) {
		return null;
	}

	return (
		<Stack display={"flex"} direction={"column"} justifyContent={"flex-start"} alignItems={"flex-start"} marginTop={1} gap={1}>
			<Typography className="!font-montserrat !text-[16px] !text-default !font-medium">Data Source</Typography>
			<RadioGroup name="radio-buttons-group" value={dataSource} onChange={handlerDataSourceChange}>
				{dataSourceList?.map((source) => (
					<FormControlLabel
						key={source._id}
						value={source.value}
						label={source.label}
						disabled={isPending}
						className="!-mt-2"
						control={
							<Radio
								className={clsx({
									"!text-primary": !isPending,
									"!text-disabled": isPending,
								})}
								disabled={isPending}
							/>
						}
						classes={{
							label: clsx("!font-sans !text-[14.5px]", {
								"!text-default": !isPending,
								"!text-disabled": isPending,
							}),
						}}
					/>
				))}
			</RadioGroup>
		</Stack>
	);
};

export default DealerManagementDataSource;
